import { useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useApp } from '../contexts/AppContext'
import { api } from '../utils/apiClient'
import type { User } from '../types'

declare global {
  interface Window {
    chmln?: {
      identify: (userId: string, userData: Record<string, unknown>) => void
      track: (eventName: string, eventData?: Record<string, unknown>) => void
      clear: () => void
    }
  }
}

const CUSTOMER_TYPE_NAMES: Record<number, string> = {
  1: 'Direct',
  2: 'License Admin',
  3: 'License User',
  4: 'Enterprise Onsite',
}

export const ChameleonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isMasquerading } = useAuth()
  const { user: appUser } = useApp() as { user: User | null }
  const initialized = useRef(false)
  const [uidHash, setUidHash] = useState<string | null>(null)

  useEffect(() => {
    const chameleonToken = import.meta.env.VITE_CHAMELEON_TOKEN
    const chameleonEnabled = import.meta.env.VITE_CHAMELEON_ENABLED === 'true'

    if (!chameleonToken || !chameleonEnabled || initialized.current) {
      return
    }

    const w = window as any
    if (!w.chmln?.root) {
      w.chmln = w.chmln || {}
      w.chmln.accountToken = chameleonToken
      w.chmln.location = window.location.href.toString()
      w.chmln.now = new Date()
      w.chmln.fastUrl = 'https://fast.chameleon.io/'

      const methods = 'identify alias track clear set show on off custom help _data'.split(' ')
      for (const method of methods) {
        const queue: any[] = []
        w.chmln[method + '_a'] = queue
        w.chmln[method] = function () {
          queue.push(arguments)
        }
      }

      const script = document.createElement('script')
      script.src = `${w.chmln.fastUrl}messo/${chameleonToken}/messo.min.js`
      script.async = true
      document.head.appendChild(script)
    }

    initialized.current = true
  }, [])

  useEffect(() => {
    const chameleonToken = import.meta.env.VITE_CHAMELEON_TOKEN
    const chameleonEnabled = import.meta.env.VITE_CHAMELEON_ENABLED === 'true'

    if (!chameleonToken || !chameleonEnabled) return
    if (!isAuthenticated || !appUser || isMasquerading?.()) return

    api.getChameleonHash()
      .then(response => {
        if (response.data?.uid_hash) {
          setUidHash(response.data.uid_hash)
        }
      })
      .catch(error => {
        console.error('Failed to fetch Chameleon hash:', error)
      })
  }, [isAuthenticated, appUser, isMasquerading])

  useEffect(() => {
    const chameleonToken = import.meta.env.VITE_CHAMELEON_TOKEN
    const chameleonEnabled = import.meta.env.VITE_CHAMELEON_ENABLED === 'true'

    if (!chameleonToken || !chameleonEnabled) return
    if (!isAuthenticated || !appUser || isMasquerading?.()) return
    if (!uidHash) return

    const customerTypeId = appUser.customerTypeId as number | undefined

    window.chmln?.identify(String(appUser.id), {
      uid_hash: uidHash,
      created_at: appUser.createdAt || appUser.created,
      portal_id: appUser.salesforceSfid || appUser.salesforceId,
      locale: appUser.preferredLocale,
      createdDate: appUser.createdAt || appUser.created,

      // Membership plan identity (server-derived, channel-agnostic):
      // 'Starter' | 'Essentials' | 'Premier' | null
      membership_plan_type: appUser.membershipPlanType ?? null,

      // How the user obtained their membership (server-derived):
      // 'Paid Active' | 'Signup Trial' | 'Program Trial' | 'Assigned' | null
      membership_status: appUser.membershipStatus ?? null,

      // Customer type
      customer_type: customerTypeId ? (CUSTOMER_TYPE_NAMES[customerTypeId] ?? null) : null,
      is_license_user: appUser.isLicenseUser ?? false,

      // Training event dates
      training_event_start_date: appUser.trainingEventStartDate ?? null,
      training_event_end_date: appUser.trainingEventEndDate ?? null,
    })
  }, [isAuthenticated, appUser, isMasquerading, uidHash])

  return <>{children}</>
}
